import {
    useForm,
    Controller,
    type SubmitHandler } from "react-hook-form"
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import { useNavigate } from "react-router-dom"

import useFormLocalStorage from "@/hooks/use-form-local-storage"
import { APP_VERSION, COLLECTION_PERIOD_LABEL_MAP, HISTORY_RETURN_PARAM_KEY, PAYER_TYPE_LABEL_MAP, ROUTE_SEGMENTS } from "@/constants/general"
import Form from "@/components/forms/reusables/form"
import useFormStates from "@/hooks/use-form-states"
import {
    type YearlyFeePayerSingle,
    type YearlyFeePayerFormInput,
    CollectionPeriod,
    PayerType,
} from "@/types"
import {
    useGenerateYearlyFeePayerFromScratch,
    useUpdateYearlyFeePayer,
} from "@/hooks/api/use-yearly-fee-payers"
import RHFNumberTextfield from "@/components/form-fields/rhf-number-textfield"
import RHFTextfield from "@/components/form-fields/rhf-textfield"
import { convertFromCents } from "@/helpers"
import CollectionPointFilter from "../filters/collection-point-filter"

const YearlyFeePayerForm = ({
    yearlyFeePayer,
    yearlyFeeCollectionId,
    closeCallback,
} : {
    yearlyFeePayer?: YearlyFeePayerSingle,
    yearlyFeeCollectionId?: number,
    closeCallback?: () => void,
}) => {

    const formDataId = `yearlyFeePayerForm-${yearlyFeePayer?.id}-v${APP_VERSION}`

    const initialValues = {
        name: yearlyFeePayer ? yearlyFeePayer.name : '',
        requested_amount_cents: yearlyFeePayer ? convertFromCents(yearlyFeePayer.requested_amount_cents) : '',
        collection_period: yearlyFeePayer ? yearlyFeePayer.collection_period : CollectionPeriod.yearly,
        payer_type: yearlyFeePayer ? yearlyFeePayer.payer_type : PayerType.per_person,
        email_address: yearlyFeePayer ? yearlyFeePayer.email_address : '',
        phone_number: yearlyFeePayer ? yearlyFeePayer.phone_number : '',
        account_number: yearlyFeePayer ? yearlyFeePayer.account_number : '',
        variable_symbol: yearlyFeePayer ? yearlyFeePayer.variable_symbol : '',
        household_id: yearlyFeePayer ? yearlyFeePayer.household?.id : null,
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const [isLoadedFromLocalStorage, handleFormReset] = useFormLocalStorage({
        control,
        formDataId,
        reset,
        initialValues
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
        setError,
        error,
    } = useFormStates(handleFormReset)

    const navigate = useNavigate()

    const mutationCreate = useGenerateYearlyFeePayerFromScratch(
        yearlyFeeCollectionId,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback(data) {
                if(data.id) {
                    navigate(`${ROUTE_SEGMENTS.yearlyFeePayers}/${data.id}?${HISTORY_RETURN_PARAM_KEY}=true`) 
                }
            },
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
        },
    )

    const mutationUpdate = useUpdateYearlyFeePayer(
        yearlyFeePayer?.id,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
        },
    )

    const onSubmit: SubmitHandler<YearlyFeePayerFormInput> = data => {
        setRequestInProgress(true)
        if (yearlyFeePayer?.id) {
            mutationUpdate.mutate(data)
        } else {
            mutationCreate.mutate(data)
        }
    }

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            heading={yearlyFeePayer ? 'Upravit poplatníka' : 'Přidat poplatníka manuálně'}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={isLoadedFromLocalStorage}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            buttonText={yearlyFeePayer ? 'Upravit' : 'Přidat poplatníka'}
            error={error}
            setError={setError}
        >
            {!yearlyFeePayer && (
                <Controller
                    name="household_id"
                    control={control}
                    render={({ field }) => (
                        <CollectionPointFilter
                            selectedId={field.value ?? null}
                            setSelectedId={field.onChange}
                            label="Svozové místo"
                        />
                    )}
                />
            )}
            <RHFTextfield
                name="name"
                control={control}
                label="Jméno a příjmení"
                required
            />
            <RHFNumberTextfield
                name="variable_symbol"
                control={control}
                label="Variabilní symbol"
                required
                disallowNegative
                decimalScale={0}
            />
            <RHFNumberTextfield
                name="requested_amount_cents"
                control={control}
                label="Výše poplatku"
                endAdornment="Kč"
                decimalScale={2}
                thousandSeparator
                disallowNegative
                required
            />
            {/* <FormControl fullWidth>
                <InputLabel id="year-select-label">Perioda výběru</InputLabel>
                <Controller
                    name="collection_period"
                    control={control}
                    render={({ field }) => (
                        <Select {...field} label="Perioda výběru">
                            <MenuItem value={CollectionPeriod.yearly}>{COLLECTION_PERIOD_LABEL_MAP[CollectionPeriod.yearly]}</MenuItem>
                            <MenuItem value={CollectionPeriod.semiannual}>{COLLECTION_PERIOD_LABEL_MAP[CollectionPeriod.semiannual]}</MenuItem>
                            <MenuItem value={CollectionPeriod.monthly}>{COLLECTION_PERIOD_LABEL_MAP[CollectionPeriod.monthly]}</MenuItem>
                        </Select>
                    )}
                />
            </FormControl> */}
            <FormControl fullWidth>
                <InputLabel id="year-select-label">Platí za</InputLabel>
                <Controller
                    name="payer_type"
                    control={control}
                    render={({ field }) => (
                        <Select {...field} label="Platí za">
                            <MenuItem value={PayerType.per_person}>{PAYER_TYPE_LABEL_MAP[PayerType.per_person]}</MenuItem>
                            <MenuItem value={PayerType.per_household}>{PAYER_TYPE_LABEL_MAP[PayerType.per_household]}</MenuItem>
                        </Select>
                    )}
                />
            </FormControl>
            <RHFTextfield
                name="email_address"
                control={control}
                label="Email"
                type="email"
            />
            <RHFTextfield
                name="phone_number"
                control={control}
                label="Telefon"
                type="tel"
            />
            <RHFTextfield
                name="account_number"
                control={control}
                label="Číslo bank. účtu"
            />
        </Form>
    )
}

export default YearlyFeePayerForm
